import React, { CSSProperties, useCallback, useMemo, useState } from 'react';
import {
    FormControl,
    MenuItem,
    Palette,
    PaletteColor,
    Select,
    SelectChangeEvent,
    SvgIconProps,
    Tooltip,
    styled,
    useTheme,
} from '@mui/material';
import { MANTY_COLORS } from 'business/constants';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useCancellableDebounce } from 'hooks/helpers';

type StatusSelectorVariantT = 'filled' | 'outlined';

export type StatusSelectorOptions<T> = {
    label: string;
    value: T;
    color: keyof Palette;
};

type StatusSelectorProps<T> = {
    variant: StatusSelectorVariantT;
    options: StatusSelectorOptions<T>[];
    onChange: (option: StatusSelectorOptions<T>) => void;
    canEdit?: boolean;
    tooltipValue?: string;
    defaultOption?: StatusSelectorOptions<T>;
    width?: 'sm' | 'md' | 'lg' | 'full-width';
    renderValue?: (value: string) => React.ReactNode;
};

const StySelect = styled(Select)<{
    bgColor: CSSProperties['color'];
    variant: StatusSelectorVariantT;
}>(({ bgColor, variant }) => {
    return variant === 'filled'
        ? {
              margin: 0,

              backgroundColor: bgColor,
              color: MANTY_COLORS.white,
              borderRadius: '8px',
              '&::before, &::after': {
                  display: 'none',
              },
              '&:hover': {
                  backgroundColor: bgColor,
                  color: MANTY_COLORS.white,
              },
              '&.Mui-selected': {
                  backgroundColor: bgColor,
                  color: MANTY_COLORS.white,
              },
              '&.Mui-focused': {
                  backgroundColor: bgColor,
                  color: MANTY_COLORS.white,
              },
              '& .MuiFilledInput-input': {
                  padding: '2px 14px',
              },
              '&.Mui-disabled': {
                  backgroundColor: bgColor,
                  color: MANTY_COLORS.white,
                  opacity: 1,
                  '& .MuiFilledInput-input': {
                      padding: '2px 14px',
                      '-webkit-text-fill-color': MANTY_COLORS.white,
                  },
              },
          }
        : {
              fontSize: '12px',
              color: bgColor,
              '&.Mui-selected': {
                  border: `1px solid ${bgColor}`,
                  color: bgColor,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${bgColor}`,
                  color: bgColor,
              },
              '& .MuiOutlinedInput-input': {
                  padding: '2px 14px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${bgColor}`,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${bgColor}`,
              },
              '&.Mui-disabled': {
                  color: bgColor,
                  opacity: 1,
                  '& .MuiOutlinedInput-input': {
                      padding: '2px 14px',
                      '-webkit-text-fill-color': bgColor,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                      border: `1px solid ${bgColor}`,
                  },
              },
          };
});

const StyExpandMoreIcon = styled(ExpandMoreIcon)<{
    bgColor: CSSProperties['color'];
    variant: StatusSelectorVariantT;
}>(({ bgColor, variant }) => ({
    color: variant === 'filled' ? MANTY_COLORS.white : bgColor,
    '&.MuiSvgIcon-root': {
        color: variant === 'filled' ? MANTY_COLORS.white : bgColor,
    },
}));

const StatusSelector = <T,>({
    variant,
    options,
    onChange,
    canEdit = true,
    tooltipValue = '',
    width = 'full-width',
    defaultOption,
    renderValue,
}: StatusSelectorProps<T>) => {
    const theme = useTheme();
    const initialOption = defaultOption || options[0];
    const [selectedOption, setSelectedOption] = React.useState<
        StatusSelectorOptions<T> | undefined
    >(initialOption);
    const [tooltipOpened, setTooltipOpened] = useState(false);

    const widthStyles: Record<string, string | number> = {
        sm: 90,
        md: 180,
        lg: 300,
        'full-width': '100%',
    };

    const handleChange = (event: SelectChangeEvent) => {
        const selectedValue = event.target.value;
        const optionClicked = options.find(
            (option) => option.value === selectedValue,
        );

        if (optionClicked) {
            setSelectedOption(optionClicked);
            onChange(optionClicked);
        }
    };

    const {
        debouncedFunction: debouncedDisplayTooltip,
        cancel: cancelDisplayTooltip,
    } = useCancellableDebounce(() => setTooltipOpened(true), 1000);

    const hideOrCancelDisplayTooltip = () => {
        cancelDisplayTooltip();
        setTooltipOpened(false);
    };

    const bgColor = useMemo(() => {
        if (!selectedOption) return initialOption.color;
        if (typeof theme.palette[selectedOption.color] === 'string')
            return theme.palette[
                selectedOption.color
            ] as CSSProperties['color'];
        if (typeof theme.palette[selectedOption.color] === 'object')
            return (theme.palette[selectedOption.color] as PaletteColor).main;
        return theme.palette.muiBlack;
    }, [selectedOption]);

    const iconComponent = useCallback(
        (props: SvgIconProps) =>
            canEdit ? (
                <StyExpandMoreIcon
                    {...props}
                    variant={variant}
                    bgColor={bgColor}
                />
            ) : (
                <></>
            ),
        [variant, bgColor, canEdit],
    );

    return (
        <FormControl sx={{ width: widthStyles[width] }}>
            <Tooltip
                title={tooltipValue || (selectedOption?.value as string)}
                open={tooltipOpened}
            >
                <StySelect
                    labelId="status-selector-label"
                    id="status-selector"
                    value={selectedOption?.value}
                    onChange={handleChange}
                    variant={variant}
                    bgColor={bgColor}
                    disabled={!canEdit}
                    renderValue={renderValue}
                    IconComponent={iconComponent}
                    onMouseEnter={debouncedDisplayTooltip}
                    onMouseLeave={hideOrCancelDisplayTooltip}
                    onOpen={hideOrCancelDisplayTooltip}
                    sx={{
                        fontSize: theme.typography.body2.fontSize,
                        letterSpacing: theme.typography.body2.letterSpacing,
                        fontWeight: theme.typography.body2.fontWeight,
                    }}
                    onClick={hideOrCancelDisplayTooltip}
                >
                    {options.map((option) => (
                        <MenuItem
                            key={option.label}
                            value={option.value as string}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </StySelect>
            </Tooltip>
        </FormControl>
    );
};

export default StatusSelector;
